import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';
import { planData } from '../../i18n/translations';

/**
 * Folie 19 — Solution Pläne kombiniert mit VIP-Paketen.
 * Same visual language as SectionPlaene, but with three additional highlight rows
 * showing Rabatt Total inkl. VIP+1% / +2% / +3%. SP1 omitted, SP4 = recommended.
 */
export default function SectionPlaeneVIP() {
  const { t, lang } = useT();
  const oneYear = t.plaene.oneYear;
  const L = t.plaeneVIP.rowLabels;
  const FROM = { de: 'ab', en: 'from', hu: 'min.', tr: 'min.' };
  const from = FROM[lang] || 'ab';

  const cols = planData; // 7 plans
  const spNum = (p) => parseInt(String(p.id).replace(/\D/g, ''), 10) || 0;

  const rows = [
    { key: 'delivery',       label: L.delivery,       value: () => oneYear },
    { key: 'price',          label: L.price,          value: (p) => `${from} ${p.from}` },
    { key: 'carat',          label: L.carat,          value: (p) => (p.caratFrom ? `${from} ${p.carat}` : p.carat) },
    { key: 'monthly',        label: L.monthly,        value: (p) => p.monthly },
    { key: 'totalDiscount',  label: L.totalDiscount,  value: (p) => p.total, emphasise: true },
    { key: 'vip1',           label: L.vip1,           value: (p) => (spNum(p) >= 2 ? p.vip1 : '—'), vipTier: 1 },
    { key: 'vip2',           label: L.vip2,           value: (p) => (spNum(p) >= 2 ? p.vip2 : '—'), vipTier: 2 },
    { key: 'vip3',           label: L.vip3,           value: (p) => (spNum(p) >= 3 ? p.vip3 : '—'), vipTier: 3 },
  ];

  return (
    <SectionShell id="plaene-vip" className="">
     <div>
      <Reveal>
        <div className="overline mb-4" style={{ fontSize: 'clamp(0.8rem, 0.9vw, 1.1rem)' }}>{t.plaeneVIP.overline}</div>
        <h2
          className="font-display tracking-tight leading-[1.02] mb-5 text-white whitespace-nowrap"
          style={{ fontSize: 'clamp(2.4rem, 4.6vw, 5.2rem)' }}
        >
          {t.plaeneVIP.title}
        </h2>
        <p
          className="font-ui text-white/70 mb-10 max-w-[86ch] leading-relaxed"
          style={{ fontSize: 'clamp(1.05rem, 1.4vw, 1.55rem)' }}
        >
          {t.plaeneVIP.sub}
        </p>
      </Reveal>

      <Reveal>
        <div className="overflow-x-auto -mx-4 sm:mx-0 pb-2" data-testid="plans-vip-scroll">
          <div
            className="min-w-[880px] lg:min-w-0 px-4 sm:px-0"
            style={{ paddingTop: '10px' }}
            data-testid="plans-vip-grid"
          >
            <div
              className="grid gap-x-3"
              style={{
                gridTemplateColumns: 'minmax(180px, 220px) repeat(7, minmax(0, 1fr))',
                gridTemplateRows: 'auto repeat(8, minmax(52px, auto))',
                rowGap: 0,
              }}
            >
              {/* Card backgrounds — SP4 gets the same "recommended" gold
                  frame + "Empfohlen" badge as on the Solution-Pläne slide */}
              {cols.map((p, i) => (
                <div
                  key={`bg-${p.id}`}
                  aria-hidden
                  data-testid={`plan-vip-col-${p.id}`}
                  className={`plan-col ${p.recommended ? 'plan-col--recommended' : ''}`}
                  style={{ gridColumn: i + 2, gridRow: '1 / -1' }}
                >
                  {p.recommended && (
                    <span className="plan-badge-gold" data-testid="plan-vip-recommended-badge">
                      {t.plaene.recommended}
                    </span>
                  )}
                </div>
              ))}

              {/* Row highlight bands — Rabatt (Monat) subtle, Rabatt (Total) strong */}
              {rows.map((row, rIdx) => {
                if (row.key !== 'monthly' && row.key !== 'totalDiscount') return null;
                return (
                  <div
                    key={`band-${row.key}`}
                    aria-hidden
                    className={`plan-row-band ${row.key === 'totalDiscount' ? 'plan-row-band--strong' : ''}`}
                    style={{ gridColumn: '1 / -1', gridRow: rIdx + 2, zIndex: 1 }}
                    data-testid={`plan-vip-row-band-${row.key}`}
                  />
                );
              })}

              {/* Header row */}
              <div style={{ gridColumn: 1, gridRow: 1 }} className="flex items-end pb-3 pr-4">
                <span className="overline text-white/45 font-semibold" style={{ fontSize: 'clamp(0.8rem, 0.95vw, 1.1rem)' }}>{t.plaene.headers.plan}</span>
              </div>
              {cols.map((p, i) => (
                <div
                  key={`h-${p.id}`}
                  style={{ gridColumn: i + 2, gridRow: 1, position: 'relative', zIndex: 2, fontSize: 'clamp(1.5rem, 1.9vw, 2.2rem)' }}
                  className={`flex items-end justify-center pb-3 pt-5 font-display font-semibold ${p.recommended ? 'text-[#f2d979]' : 'text-white'}`}
                >
                  {p.id}
                </div>
              ))}

              {/* Data rows */}
              {rows.map((row, rIdx) => {
                const gridRow = rIdx + 2;
                const isVip = !!row.vipTier;
                return (
                  <React.Fragment key={row.key}>
                    <div
                      style={{ gridColumn: 1, gridRow }}
                      className="flex items-center pr-4 border-t border-white/[0.05]"
                    >
                      <span
                        className={`font-ui tracking-wide font-semibold ${
                          isVip ? 'text-[#f2d979]' : 'text-white/85'
                        }`}
                        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.4rem)' }}
                      >
                        {row.label}
                      </span>
                    </div>
                    {cols.map((p, cIdx) => (
                      <div
                        key={`c-${row.key}-${p.id}`}
                        style={{
                          gridColumn: cIdx + 2,
                          gridRow,
                          position: 'relative',
                          zIndex: 2,
                          fontSize: isVip
                            ? 'clamp(1.15rem, 1.5vw, 1.75rem)'
                            : row.emphasise
                            ? 'clamp(1.4rem, 1.75vw, 2rem)'
                            : 'clamp(1.05rem, 1.35vw, 1.55rem)',
                        }}
                        className={`flex items-center justify-center border-t border-white/[0.05] font-ui font-semibold ${
                          isVip
                            ? 'font-display text-[#f2d979]'
                            : row.emphasise
                            ? p.recommended
                              ? 'font-display text-[#f2d979] font-bold'
                              : 'font-display text-white font-bold'
                            : p.recommended
                            ? 'text-white'
                            : 'text-white/90'
                        }`}
                        data-testid={`plan-vip-cell-${p.id}-${row.key}`}
                      >
                        {row.value(p)}
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={220}>
        <p className="font-ui italic text-white/40 mt-10 max-w-[95ch] leading-relaxed"
           style={{ fontSize: 'clamp(0.9rem, 1.05vw, 1.15rem)' }}>
          {t.plaeneVIP.footnote}
        </p>
      </Reveal>
     </div>
    </SectionShell>
  );
}
